import { ChangeDetectionStrategy, Component, Inject, inject } from '@angular/core';
import { DIALOG_DATA, DialogModule, DialogRef } from '@angular/cdk/dialog';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { VideoPlayerComponent, VideoProgress } from '@libs/video-player';
import { TagEvent, TagPusherService } from '@libs/shared-services';
import { PreviewVideoData } from '@libs/media-preview';
import {
  PreviewAttachmentDialogData,
  PreviewImageAttachmentDialogData,
  PreviewVideoAttachmentDialogData,
} from './attachment-preview-dialog.vm';
import { PreviewAttachmentDialogService } from './attachment-preview-dialog.service';

@Component({
  selector: 'lib-attachment-preview-dialog',
  templateUrl: './attachment-preview-dialog.component.html',
  styleUrls: ['./attachment-preview-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, SvgIconComponent, DialogModule, MatMenuModule, VideoPlayerComponent],
  providers: [PreviewAttachmentDialogService],
})
export class PreviewAttachmentDialogComponent {
  private readonly previewAttachmentDialogService = inject(PreviewAttachmentDialogService);
  private readonly dialogRef = inject(DialogRef);
  private readonly tagPusherService = inject(TagPusherService);

  constructor(@Inject(DIALOG_DATA) public readonly data: PreviewAttachmentDialogData) {}

  protected onCloseClick(): void {
    this.dialogRef.close();
  }

  protected attachmentIsVideo(data: PreviewAttachmentDialogData): data is PreviewVideoAttachmentDialogData {
    return data.type === 'video';
  }

  protected attachmentIsImage(data: PreviewAttachmentDialogData): data is PreviewImageAttachmentDialogData {
    return data.type === 'image';
  }

  protected onDownloadButtonClick(): void {
    this.previewAttachmentDialogService.downloadButtonClick();
  }

  protected onDownloadAttachmentsClick(): void {
    this.previewAttachmentDialogService.downloadAttachment(this.data.id!, this.data.file);
  }

  public onVideoStarted(): void {
    this.tagPusherService.pushTag({
      event: TagEvent.VideoStarted,
      video_id: this.data.id,
      video_title: (this.data.fileData as PreviewVideoData).videoUrl,
      module: this.data.tutorialModule,
      contract_id: this.data.installationContractId,
      type: 'full_mode',
    });
  }

  public onVideoFinished(): void {
    this.tagPusherService.pushTag({
      event: TagEvent.VideoFinished,
      video_id: this.data.id,
      video_title: (this.data.fileData as PreviewVideoData).videoUrl,
      module: this.data.tutorialModule,
      contract_id: this.data.installationContractId,
      type: 'full_mode',
    });
  }

  public onVideoDisposed({ percentWatched }: VideoProgress): void {
    this.tagPusherService.pushTag({
      event: TagEvent.VideoAborted,
      video_id: this.data.id,
      video_title: (this.data.fileData as PreviewVideoData).videoUrl,
      percentage: percentWatched,
      module: this.data.tutorialModule,
      contract_id: this.data.installationContractId,
      type: 'full_mode',
    });
  }
}
